/**
 * HeroPrompt — the conversational prompt box on hero Frame 1.
 * Lives INSIDE the phase-1 overlay, so the existing GSAP scrub fades and
 * scales it away with the intro copy as scroll progress begins (§8) — no
 * extra scroll wiring, no React re-renders in the scroll loop.
 * Every prompt is handed to Dashboard, where Piixel decides whether the user
 * wants to keep planning or is ready to create a project. Guests authenticate
 * first; no project is created from the landing page itself.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAuthed } from '../lib/api';
import { attachPromptPlaceholderTypewriter, DEFAULT_APP_PROMPT } from '../lib/promptIdeas';

export const PENDING_PROMPT_KEY = 'helix-pending-prompt';

export default function HeroPrompt() {
  const nav = useNavigate();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return undefined;
    return attachPromptPlaceholderTypewriter(textarea);
  }, []);

  const go = async (): Promise<void> => {
    const prompt = text.trim();
    if (!prompt || busy) return;
    try { sessionStorage.setItem(PENDING_PROMPT_KEY, prompt); } catch { /* private mode */ }
    setBusy(true);
    nav(isAuthed() ? '/dashboard' : '/signup');
  };

  return (
    <div className="hero-prompt" onWheel={(e) => e.stopPropagation()}>
      <textarea
        ref={textareaRef}
        aria-label="Describe the app you want to build"
        rows={2}
        placeholder={DEFAULT_APP_PROMPT}
        value={text}
        disabled={busy}
        onChange={(e) => {
          setText(e.target.value);
          e.currentTarget.style.height = 'auto';
          e.currentTarget.style.height = `${Math.min(e.currentTarget.scrollHeight, 200)}px`;
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void go(); }
        }}
      />
      <div className="hero-prompt-foot">
        <span className="hero-prompt-hint">Enter to continue · shift+Enter for a new line</span>
        <button
          className="hero-prompt-send"
          data-active={!!text.trim()}
          disabled={busy}
          onClick={() => void go()}
        >
          {busy ? 'Opening…' : 'Continue'}
        </button>
      </div>
    </div>
  );
}
